/**
 * @file index.tsx
 *
 * @fileoverview Renders the entire filters section.
 */
import React from 'react';
import { processFacets } from '~/jobiqo/components/Search/utils/searchHelpers';
import FacetFilters from '~/jobiqo/components/Search/facets/facet-filters';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Facet, FacetConfig } from '~/jobiqo/models/search';
import { ParsedUrlQuery } from 'querystring';
import { OrganizationSearchInputForm } from '~/jobiqo/components/OrganizationSearch/SearchInputForm';
import { FormattedMessage } from 'react-intl';
import Link from '~/jobiqo/components/Link';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { UndoAltSolidIcon } from '~/vcl/icons/UndoAltSolid';

export interface OrganizationFiltersSectionProps {
  /**
   * Gets which filters are currently selected.
   */
  getFilterSelectedItems: (querystring: any, facet: Facet) => string[];
  /**
   * Callback when a filter is toggled (added / removed).
   */
  toggleFilter: (filter: string, selectedItems: string[]) => void;
  /**
   * The current querystring object.
   */
  querystring: ParsedUrlQuery;
  /**
   * The current pathname.
   */
  pathname: string;
  /**
   * The list of facets / filters being used.
   */
  facets: Facet[];
  /**
   * If the search form should be hidden (hidden when viewing job detail on mobile).
   */
  hiddenForm: boolean;
  /**
   * The facets configurations.
   */
  organizationSearchFacetsConfig: FacetConfig[];
}

export const OrganizationFiltersSection = ({
  getFilterSelectedItems,
  querystring,
  pathname,
  toggleFilter,
  facets,
  hiddenForm,
  organizationSearchFacetsConfig,
}: OrganizationFiltersSectionProps) => {
  const { ORGANIZATIONS } = useFrontendConfig();
  return (
    <div className={hiddenForm ? 'hidden' : ''}>
      <OrganizationSearchInputForm querystring={querystring} />
      <Spacing size="medium" />
      <FlexItems alignItems="center" justifyContent="center">
        <FacetFilters
          getFacetSelectedItems={(facet) =>
            getFilterSelectedItems(querystring, facet)
          }
          onFacetToggle={toggleFilter}
          facets={processFacets(facets, organizationSearchFacetsConfig)}
          querystring={querystring}
          pathname={pathname}
        />
        <HideMobile>
          {Object.keys(querystring).length > 0 && (
            <FlexItem marginLeft={6} marginTop={6}>
              <Link href={ORGANIZATIONS.ORGANIZATIONS_SEARCH_PAGE}>
                <>
                  <UndoAltSolidIcon
                    aria-hidden="true"
                    width="15"
                    height="15"
                    title="Undo"
                  />
                  &nbsp;
                  <FormattedMessage
                    id={'current_search_reset'}
                    defaultMessage={'Reset Search'}
                  />
                </>
              </Link>
            </FlexItem>
          )}
        </HideMobile>
      </FlexItems>
      <Spacing size="large" />
    </div>
  );
};
